import type XGPlayer from 'xgplayer';

/**
 * @description 从西瓜播放器实例判断是否处于静音或音量为 0（与页内「静音」体验对齐）
 * @param player - XGPlayer 实例
 */
export function readInlinePlayerMutedFromXG(player: XGPlayer): boolean {
  const vid = (player as { video?: HTMLVideoElement; }).video;
  if (vid) {
    return vid.muted === true || vid.volume === 0;
  }
  const pv = (player as { volume?: number; }).volume;
  return typeof pv === 'number' && pv === 0;
}

/** 页内（非全屏）视频播放器在打开公共媒体预览前可读取的进度、是否在播及是否静音 */
export interface PreviewInlineVideoPlaybackSnapshot {
  currentTime: number;
  playing: boolean;
  /** 页内为静音或音量为 0 时为 true */
  muted: boolean;
}

/** 与 {@link PreviewInlineVideoPlaybackSnapshot} 配套的暂停能力，由注册方实现 */
export interface PreviewInlineVideoSource {
  getPlaybackSnapshot: () => PreviewInlineVideoPlaybackSnapshot;
  pause: () => void;
}

const sourcesByKey = new Map<string, PreviewInlineVideoSource>();

/**
 * @description 注册一个页内视频源：调用方用稳定 key（如媒体唯一 id）关联，便于打开全屏预览前读取快照
 * @param sourceKey - 调用方约定的稳定字符串 key
 * @param source - 快照与暂停实现
 */
export function registerPreviewInlineVideoSource(sourceKey: string, source: PreviewInlineVideoSource): void {
  sourcesByKey.set(String(sourceKey), source);
}

/**
 * @description 注销页内视频源，避免卸载后仍被读取
 * @param sourceKey - 与注册时一致的 key
 */
export function unregisterPreviewInlineVideoSource(sourceKey: string): void {
  sourcesByKey.delete(String(sourceKey));
}

/**
 * @description 在打开全屏预览前读取已注册源的播放快照；未注册时返回 0 与未播放
 * @param sourceKey - 与注册时一致的 key
 */
export function getPreviewInlineVideoSnapshot(sourceKey: string): PreviewInlineVideoPlaybackSnapshot {
  const source = sourcesByKey.get(String(sourceKey));
  if (!source) return { currentTime: 0, playing: false, muted: false };
  return source.getPlaybackSnapshot();
}
